import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLinkClickHandler } from 'react-router'
import { ArrowLeft, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { moderationApi } from '../../services/api'
import { ROLES, ROLE_HIERARCHY } from '../../constants/roles'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useOrganization } from '../../hooks/useOrganization'
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  DataView,
  Dialog,
  Field,
  Input,
  LinkButton,
  PageHeader,
  PageLayout,
  SegmentedControl,
  Select,
  StatePanel,
  StatusBadge,
  Switch,
  Textarea,
} from '../../components/common'
import { HighlightedText } from '../../components/moderation/HighlightedText'
import ModerationStateBanner from '../../components/moderation/ModerationStateBanner'
import { findingLabel } from '../../components/moderation/moderationFindings'
import { useModerationStatus } from '../../components/moderation/useModerationStatus'
import {
  EMPTY_RULE_FORM,
  MATCH_KIND_OPTIONS,
  MAX_PHRASE_LENGTH,
  PREVIEW_FAILED,
  PREVIEW_LOADING,
  RULE_FILTERS,
  TEST_FIELDS,
  actionLabel,
  actionOptions,
  actionTone,
  breadthSentence,
  categoryLabel,
  classifyRuleError,
  filterRules,
  hasPlatformRules,
  matchKindLabel,
  measuredChange,
  previewLines,
  previewRequest,
  regulationOptions,
  ruleForFinding,
  ruleForm,
  ruleFormProblems,
  ruleFormReady,
  rulePayload,
  ruleRegulationLabel,
  ruleUpdatePayload,
  testRequest,
  testStatusView,
  tooBroadWarning,
} from '../../components/moderation/phraseRuleModel'
import { formatRegulationName } from '../../utils/regulationLabels'
import styles from './ModerationRulesPage.module.css'

const MODERATION_QUEUE_PATH = '/dashboard/feed/moderation'

const EMPTY = []
const EXCERPT_CLASSES = { root: styles.excerpt, label: styles.excerptLabel, mark: styles.mark }
const EMPTY_TEST_INPUT = { title: '', text: '' }
// The add and edit preview runs once typing pauses for this long.
const PREVIEW_DEBOUNCE_MS = 400
// A confirmation of a too-broad rule holds only for what the admin saw: a
// change to any of these fields asks for it again.
const BREADTH_FIELDS = new Set(['phrase', 'match_kind', 'action'])

// Wager's platform word list is internal: the API sends its rows, and names
// the platform rule behind a finding, only to internal org admins and super
// admins. A brand admin is told the rules exist, never what they are.
const PLATFORM_NOTE_INTERNAL = 'Platform rules apply to every brand and cannot be changed here.'
const PLATFORM_NOTE_BRAND = "Wager's own rules also apply to every brand and are not listed here."
const TEST_INTRO_INTERNAL = "Check text against this brand's active rules and the platform rules."
const TEST_INTRO_BRAND = 'Check text against the rules that apply to this brand.'

function scopeSentence(scope = []) {
  if (!scope.length) return 'This brand routes to no regulations, so only rules for all regulations were checked.'
  const names = scope.map((code) => formatRegulationName(code) || code)
  return `Checked for ${scope.length} regulation${scope.length === 1 ? '' : 's'}: ${names.join(', ')}.`
}

function TestResult({ outcome, rules, changed, moderationStatus }) {
  const { input, result } = outcome
  const view = testStatusView(result.status, moderationStatus)
  const findings = Array.isArray(result.findings) ? result.findings : EMPTY
  return (
    <>
      <div className={styles.testStatus}>
        <StatusBadge status={view.tone}>{view.label}</StatusBadge>
        {view.description ? <p>{view.description}</p> : null}
      </div>
      {TEST_FIELDS.map(({ field, label }) => (
        <HighlightedText
          key={field}
          label={label}
          field={field}
          text={input[field] || ''}
          findings={findings}
          classes={EXCERPT_CLASSES}
        />
      ))}
      {findings.length ? (
        <ul className={styles.findingList} aria-label="Matched rules">
          {findings.map((finding, index) => {
            const rule = ruleForFinding(finding, rules)
            return (
              <li key={`${finding.source}-${finding.field}-${finding.offset}-${index}`}>
                <StatusBadge status={finding.severity === 'block' ? 'error' : 'warning'} size="small">
                  {finding.severity === 'block' ? 'Block' : 'Flag'}
                </StatusBadge>
                <span>{findingLabel(finding, input)}</span>
                {rule ? (
                  <span className={styles.ruleRef}>
                    Rule “{rule.phrase}”{rule.read_only ? ' (platform)' : ''}
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
      <p className={styles.scope}>{scopeSentence(result.scope || EMPTY)}</p>
      {changed ? <p className={styles.stale}>You changed the text since this test. Test it again to see the new result.</p> : null}
    </>
  )
}

const RULE_PREVIEW_QUERY = 'moderation-rule-preview'

// previewedRuleId is the rule a cached preview request names (an edit or a
// delete), or null.
function previewedRuleId(key) {
  try {
    return JSON.parse(key)?.rule_id ?? null
  } catch {
    return null
  }
}

// useRulePreview asks what a rule change, given as its JSON preview request,
// would do now. An empty key asks nothing. Every open re-runs it: the
// answer depends on the queue at that moment.
function useRulePreview(orgSlug, key) {
  return useQuery({
    queryKey: [RULE_PREVIEW_QUERY, orgSlug, key],
    queryFn: () => moderationApi.previewPhraseRule(orgSlug, JSON.parse(key)),
    enabled: Boolean(orgSlug && key),
    retry: false,
    staleTime: 0,
  })
}

// ChangePreview says what a rule change would do now: how much ordinary
// content a measured change matches, then what happens to the brand's held
// and rule-rejected posts. A too-broad refusal's numbers stand in when the
// preview did not load. It informs; it never holds the save back.
function ChangePreview({ query, waiting = false, measured = false, refusedBreadth = null }) {
  const lines = query.data ? previewLines(query.data, { measured }) : refusedBreadth ? [breadthSentence(refusedBreadth)] : null
  let body = null
  if (waiting || query.isFetching) {
    body = <p className={styles.previewNote}>{PREVIEW_LOADING}</p>
  } else if (lines) {
    body = (
      <ul className={styles.previewLines}>
        {lines.map((line) => <li key={line}>{line}</li>)}
      </ul>
    )
  } else if (query.isError) {
    body = <p className={styles.previewNote}>{PREVIEW_FAILED}</p>
  }
  return (
    <section className={styles.preview} aria-live="polite" aria-label="What this change does">
      {body ? (
        <>
          <h3 className={styles.previewTitle}>What this change does</h3>
          {body}
        </>
      ) : null}
    </section>
  )
}

// ModerationRulesPage lists the organization's phrase rules, beside the
// read-only platform rules for internal admins, edits the organization's own
// rules, and tests text against everything currently active. Brand admins
// with Feed configuration reach it from the Moderation page.
export default function ModerationRulesPage() {
  const { currentOrg } = useOrganization()
  const orgSlug = currentOrg?.slug
  const internal = (ROLE_HIERARCHY[currentOrg?.role] || 0) >= ROLE_HIERARCHY[ROLES.INTERNAL_ORG_ADMIN]
  const openQueue = useLinkClickHandler(MODERATION_QUEUE_PATH)
  const queryClient = useQueryClient()
  const statusQuery = useModerationStatus(orgSlug)
  const [filter, setFilter] = useState(RULE_FILTERS[0].value)
  // editing is null, { mode: 'create' } or { mode: 'edit', rule }.
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY_RULE_FORM)
  const [formError, setFormError] = useState(null)
  const [reloading, setReloading] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const [testInput, setTestInput] = useState(EMPTY_TEST_INPUT)
  const [testOutcome, setTestOutcome] = useState(null)
  const [testError, setTestError] = useState(null)

  // The answer depends on the viewer's role, so the role is in the key: a
  // list cached for a staff member is never shown to a brand admin who signs
  // into the same organization in this tab.
  const queryKey = ['moderation-phrase-rules', orgSlug, currentOrg?.role]
  const rulesQuery = useQuery({
    queryKey,
    queryFn: () => moderationApi.listPhraseRules(orgSlug),
    enabled: Boolean(orgSlug),
  })
  const rules = rulesQuery.data?.rules || EMPTY
  const regulations = rulesQuery.data?.regulations || EMPTY
  // The filter only matters when the API sent platform rules, which it does
  // for internal admins alone; everyone else sees the brand's rules. The role
  // is checked as well as the rows, so a cached staff answer never shows a
  // brand admin the platform list.
  const showsPlatformRules = useMemo(() => internal && hasPlatformRules(rules), [internal, rules])
  const activeFilter = showsPlatformRules ? filter : 'brand'
  const visibleRules = useMemo(() => filterRules(rules, activeFilter), [rules, activeFilter])
  const ruleCounts = useMemo(() => Object.fromEntries(
    RULE_FILTERS.map((entry) => [entry.value, filterRules(rules, entry.value).length]),
  ), [rules])

  // The editor previews the change once typing pauses; the delete dialog
  // previews the delete as soon as it opens.
  const editorPreviewRequest = editing && ruleFormReady(form)
    ? JSON.stringify(previewRequest(editing.mode, editing.rule, form))
    : ''
  const settledPreviewRequest = useDebouncedValue(editorPreviewRequest, PREVIEW_DEBOUNCE_MS)
  const editorPreviewSettled = settledPreviewRequest === editorPreviewRequest
  const editorPreview = useRulePreview(orgSlug, editorPreviewSettled ? editorPreviewRequest : '')
  const deletePreview = useRulePreview(orgSlug, deleting ? JSON.stringify(previewRequest('delete', deleting)) : '')

  const rulesChanged = (message) => {
    toast.success(message)
    // The tester ran against the old rules; its result no longer holds.
    setTestOutcome(null)
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: [RULE_PREVIEW_QUERY, orgSlug] })
  }

  // forgetRulePreviews drops the cached previews of a rule that no longer
  // exists, before rulesChanged invalidates the rest: the delete dialog's
  // preview is still mounted at that moment, and asking the server about a
  // deleted rule again answers 404.
  const forgetRulePreviews = (ruleId) => {
    const filters = {
      queryKey: [RULE_PREVIEW_QUERY, orgSlug],
      predicate: (query) => previewedRuleId(query.queryKey[2]) === ruleId,
    }
    queryClient.cancelQueries(filters)
    queryClient.removeQueries(filters)
  }

  const closeEditor = () => {
    setEditing(null)
    setForm(EMPTY_RULE_FORM)
    setFormError(null)
  }

  const saveMutation = useMutation({
    mutationFn: ({ mode, rule, values }) => (mode === 'edit'
      ? moderationApi.updatePhraseRule(orgSlug, rule.id, ruleUpdatePayload(rule, values))
      : moderationApi.createPhraseRule(orgSlug, rulePayload(values))),
    onMutate: () => setFormError(null),
    onSuccess: (_data, { mode }) => {
      closeEditor()
      rulesChanged(mode === 'edit' ? 'Phrase rule updated' : 'Phrase rule added')
    },
    onError: (error) => {
      const refusal = classifyRuleError(error)
      setFormError(refusal)
      if (refusal.kind === 'conflict' || refusal.kind === 'missing') queryClient.invalidateQueries({ queryKey })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (rule) => moderationApi.deletePhraseRule(orgSlug, rule.id, rule.version),
    onMutate: () => setDeleteError(null),
    onSuccess: (_data, rule) => {
      setDeleting(null)
      forgetRulePreviews(rule.id)
      rulesChanged('Phrase rule deleted')
    },
    onError: (error) => {
      const refusal = classifyRuleError(error, 'The rule could not be deleted.')
      if (refusal.kind === 'conflict' || refusal.kind === 'missing') {
        // The table is out of date: reload it and let the admin look again.
        setDeleting(null)
        toast.error(refusal.message)
        queryClient.invalidateQueries({ queryKey })
        return
      }
      setDeleteError(refusal.message)
    },
  })

  const testMutation = useMutation({
    mutationFn: (input) => moderationApi.testModeration(orgSlug, testRequest(input)),
    onMutate: () => setTestError(null),
    onSuccess: (result, input) => setTestOutcome({ input, result }),
    onError: (error) => {
      setTestOutcome(null)
      setTestError(classifyRuleError(error, 'The test could not run.').message)
    },
  })

  const openCreate = () => {
    setForm({ ...EMPTY_RULE_FORM })
    setFormError(null)
    setEditing({ mode: 'create' })
  }
  const openEdit = (rule) => {
    setForm(ruleForm(rule))
    setFormError(null)
    setEditing({ mode: 'edit', rule })
  }
  const dismissEditor = () => {
    if (saveMutation.isPending) return
    closeEditor()
  }
  const reloadRule = async () => {
    const ruleId = editing?.rule?.id
    if (!ruleId) return
    setReloading(true)
    try {
      const fresh = await rulesQuery.refetch()
      const latest = (fresh.data?.rules || EMPTY).find((rule) => rule.id === ruleId)
      if (!latest) {
        setFormError(classifyRuleError({ response: { status: 404 } }))
        return
      }
      setEditing({ mode: 'edit', rule: latest })
      setForm(ruleForm(latest))
      setFormError(null)
    } finally {
      setReloading(false)
    }
  }
  const setField = (field) => (event) => {
    const value = event.target.value
    if (!BREADTH_FIELDS.has(field)) {
      setForm((current) => ({ ...current, [field]: value }))
      return
    }
    // A too-broad refusal or confirmation was for the rule as it was.
    setForm((current) => ({ ...current, [field]: value, confirm_broad: false }))
    setFormError((current) => (current?.kind === 'too_broad' ? null : current))
  }
  const setTestField = (field) => (event) => {
    const value = event.target.value
    setTestInput((current) => ({ ...current, [field]: value }))
  }

  const columns = [
    {
      key: 'phrase',
      label: 'Phrase',
      rowHeader: true,
      priority: 'primary',
      render: (rule) => (
        <span className={styles.phraseCell}>
          <span className={rule.match_kind === 'regex' ? styles.pattern : styles.phrase}>{rule.phrase}</span>
          {rule.read_only ? <StatusBadge status="default" size="small">Platform</StatusBadge> : null}
        </span>
      ),
    },
    { key: 'match_kind', label: 'Match', priority: 'secondary', render: (rule) => matchKindLabel(rule.match_kind) },
    {
      key: 'action',
      label: 'Action',
      priority: 'primary',
      render: (rule) => <StatusBadge status={actionTone(rule.action)} size="small">{actionLabel(rule.action)}</StatusBadge>,
    },
    { key: 'regulation_code', label: 'Regulation', priority: 'secondary', render: (rule) => ruleRegulationLabel(rule.regulation_code) },
    { key: 'category', label: 'Category', priority: 'tertiary', render: (rule) => categoryLabel(rule.category) },
    {
      key: 'active',
      label: 'Status',
      priority: 'primary',
      render: (rule) => (
        <StatusBadge status={rule.active ? 'success' : 'default'} size="small">{rule.active ? 'Active' : 'Inactive'}</StatusBadge>
      ),
    },
    {
      key: 'note',
      label: 'Note',
      priority: 'tertiary',
      render: (rule) => (rule.note ? <span className={styles.note} title={rule.note}>{rule.note}</span> : <span className={styles.muted}>—</span>),
    },
    {
      key: 'actions',
      label: <span className="wf-visually-hidden">Actions</span>,
      align: 'right',
      priority: 'primary',
      sticky: 'end',
      render: (rule) => (rule.read_only ? (
        <span className={styles.muted}>Read-only</span>
      ) : (
        <span className={styles.rowActions}>
          <Button size="compact" variant="secondary" onClick={() => openEdit(rule)} aria-label={`Edit ${rule.phrase}`}>Edit</Button>
          <Button
            size="compact"
            variant="ghost"
            onClick={() => {
              setDeleteError(null)
              setDeleting(rule)
            }}
            aria-label={`Delete ${rule.phrase}`}
          >
            Delete
          </Button>
        </span>
      )),
    },
  ]

  if (!orgSlug) {
    return (
      <PageLayout size="wide">
        <StatePanel state="restricted" title="Select an organization" description="Choose an organization before editing its phrase rules." />
      </PageLayout>
    )
  }

  const editingMode = editing?.mode
  const formProblems = ruleFormProblems(form)
  const formReady = ruleFormReady(form)
  const matchKind = MATCH_KIND_OPTIONS.find((option) => option.value === form.match_kind)
  const actions = actionOptions(statusQuery.data)
  const action = actions.find((option) => option.value === form.action)
  const refusedBreadth = formError?.kind === 'too_broad' ? formError.breadth : null
  const tooBroad = Boolean(editorPreview.data?.too_broad || refusedBreadth)
  const testable = TEST_FIELDS.some(({ field }) => testInput[field].trim() !== '')
  const testChanged = Boolean(testOutcome) && TEST_FIELDS.some(({ field }) => (testOutcome.input[field] || '') !== testInput[field])

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Phrase rules"
        context="Feed / Moderation / Phrase rules"
        subtitle={`Phrases this brand blocks or sends to the moderation queue. ${internal ? PLATFORM_NOTE_INTERNAL : PLATFORM_NOTE_BRAND} A change applies at once to posts waiting in the moderation queue; live posts are checked against it the next time they are edited or republished.`}
        secondaryActions={(
          <LinkButton href={MODERATION_QUEUE_PATH} variant="secondary" onClick={openQueue}>
            <ArrowLeft size={17} aria-hidden="true" />
            Moderation queue
          </LinkButton>
        )}
        primaryAction={(
          <Button onClick={openCreate}>
            <Plus size={17} aria-hidden="true" />
            Add rule
          </Button>
        )}
      />

      <ModerationStateBanner orgSlug={orgSlug} context="rules" />

      {showsPlatformRules ? (
        <SegmentedControl
          label="Rules shown"
          value={activeFilter}
          onValueChange={setFilter}
          items={RULE_FILTERS.map((entry) => ({
            value: entry.value,
            label: `${entry.label} (${ruleCounts[entry.value]})`,
          }))}
        />
      ) : null}

      <DataView
        items={visibleRules}
        loading={rulesQuery.isLoading}
        error={rulesQuery.error}
        emptyTitle={activeFilter === 'brand' ? 'No brand rules yet' : 'No rules'}
        emptyDescription={activeFilter === 'brand'
          ? 'Add a phrase this brand does not allow, or one a brand admin should review before a post goes live.'
          : 'No phrase rules apply to this brand.'}
        primaryAction={activeFilter === 'brand' ? <Button onClick={openCreate}>Add rule</Button> : null}
        onRetry={() => rulesQuery.refetch()}
      >
        <DataTable
          className={styles.rulesTable}
          label="Phrase rules"
          tableLabel="Phrase rules"
          data={visibleRules}
          columns={columns}
          keyField="id"
          getRowLabel={(rule) => rule.phrase}
          density="compact"
          responsiveColumns
          searchable
          searchPlaceholder="Search phrases"
          paginate
          pageSize={25}
        />
      </DataView>

      <section className={styles.testPanel} aria-labelledby="phrase-rule-test-title">
        <div className={styles.testHeading}>
          <h2 id="phrase-rule-test-title">Test content</h2>
          <p>{internal ? TEST_INTRO_INTERNAL : TEST_INTRO_BRAND} Nothing is saved and the moderation provider is not called.</p>
        </div>
        <form
          className={styles.testForm}
          onSubmit={(event) => {
            event.preventDefault()
            if (testable && !testMutation.isPending) testMutation.mutate({ ...testInput })
          }}
        >
          <Field label="Title" optional>
            <Input value={testInput.title} onChange={setTestField('title')} />
          </Field>
          <Field label="Text">
            <Textarea value={testInput.text} onChange={setTestField('text')} rows={4} />
          </Field>
          <div className={styles.testActions}>
            <Button type="submit" variant="secondary" loading={testMutation.isPending} disabled={!testable}>Test</Button>
          </div>
        </form>
        {testError ? <Alert variant="error">{testError}</Alert> : null}
        <div className={styles.testResult} aria-live="polite">
          {testOutcome ? <TestResult outcome={testOutcome} rules={rules} changed={testChanged} moderationStatus={statusQuery.data} /> : null}
        </div>
      </section>

      <Dialog
        open={Boolean(editing)}
        title={editingMode === 'edit' ? 'Edit phrase rule' : 'Add phrase rule'}
        description="Posts waiting in the moderation queue are checked against this rule as soon as you save. Live posts keep their result until they are next edited or republished."
        dismissable={!saveMutation.isPending}
        onClose={dismissEditor}
        footer={(
          <>
            <Button variant="secondary" disabled={saveMutation.isPending} onClick={dismissEditor}>Cancel</Button>
            <Button
              type="submit"
              form="phrase-rule-form"
              loading={saveMutation.isPending}
              disabled={!formReady || formError?.kind === 'conflict' || formError?.kind === 'missing'}
            >
              {editingMode === 'edit' ? 'Save rule' : 'Add rule'}
            </Button>
          </>
        )}
      >
        <form
          id="phrase-rule-form"
          className={styles.dialogForm}
          onSubmit={(event) => {
            event.preventDefault()
            if (!formReady || saveMutation.isPending || !editing) return
            saveMutation.mutate({ mode: editing.mode, rule: editing.rule, values: form })
          }}
        >
          {formError ? (
            <Alert variant="error" className={styles.formAlert}>
              <span>{formError.message}</span>
              {formError.kind === 'conflict' ? (
                <Button size="compact" variant="secondary" loading={reloading} onClick={reloadRule}>Reload rule</Button>
              ) : null}
            </Alert>
          ) : null}
          <Field label="Phrase" required error={formProblems.phrase} help={form.match_kind === 'regex' ? 'A regular expression. It may not match empty text.' : `Up to ${MAX_PHRASE_LENGTH} characters.`}>
            <Input value={form.phrase} onChange={setField('phrase')} autoComplete="off" />
          </Field>
          <Field label="Match" help={matchKind?.description}>
            <Select value={form.match_kind} onChange={setField('match_kind')}>
              {MATCH_KIND_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
          </Field>
          <Field label="Action" help={action?.description}>
            <Select value={form.action} onChange={setField('action')}>
              {actions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
          </Field>
          <Field label="Regulation" help="Limit the rule to posts published in one regulation, or apply it everywhere.">
            <Select value={form.regulation_code} onChange={setField('regulation_code')}>
              {regulationOptions(regulations, editing?.rule?.regulation_code || '').map((option) => (
                <option key={option.value || 'all'} value={option.value}>{option.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Note" optional error={formProblems.note} help="Why the rule exists, for other admins. Not shown to editors.">
            <Textarea value={form.note} onChange={setField('note')} rows={2} />
          </Field>
          <Switch
            checked={form.active}
            onChange={(checked) => setForm((current) => ({ ...current, active: checked }))}
            label="Active"
            description="An inactive rule is kept but not matched."
          />
          <ChangePreview
            query={editorPreview}
            waiting={!editorPreviewSettled}
            measured={Boolean(editing) && measuredChange(editing.rule, form)}
            refusedBreadth={refusedBreadth}
          />
          {tooBroad ? (
            <Alert variant="warning" className={styles.tooBroad}>
              <p>{tooBroadWarning(form.action, statusQuery.data?.flagged_content)}</p>
              <Checkbox
                checked={Boolean(form.confirm_broad)}
                onChange={(event) => {
                  const { checked } = event.target
                  setForm((current) => ({ ...current, confirm_broad: checked }))
                }}
                label="Save it anyway"
              />
            </Alert>
          ) : null}
        </form>
      </Dialog>

      <Dialog
        open={Boolean(deleting)}
        title="Delete phrase rule"
        description={deleting ? `Delete the rule “${deleting.phrase}”? Posts it holds in the moderation queue, and posts it rejected, are checked again as soon as you delete it. To pause a rule instead, edit it and turn it off.` : ''}
        dismissable={!deleteMutation.isPending}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleting(null)
        }}
        footer={(
          <>
            <Button variant="secondary" disabled={deleteMutation.isPending} onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={() => deleting && deleteMutation.mutate(deleting)}>Delete rule</Button>
          </>
        )}
      >
        <ChangePreview query={deletePreview} />
        {deleteError ? <Alert variant="error">{deleteError}</Alert> : null}
      </Dialog>
    </PageLayout>
  )
}
